import {
  ArrowRightIcon,
  CornerDownLeftIcon,
  LanguagesIcon,
  LogOutIcon,
  MonitorIcon,
  MoonIcon,
  SunIcon,
} from 'lucide-react';
import { Fragment, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { useUserSearch } from '@/shared/api/directory';
import { useCurrentUserOrNull } from '@/shared/auth/hooks';
import { changeLanguage, changeTheme } from '@/shared/auth/preferences';
import { Avatar } from '@/shared/components/avatar';
import { useSidePanel } from '@/shared/components/side-panel';
import { LANGUAGES, SUPPORTED_LANGUAGES } from '@/shared/i18n/languages';
import { useDebouncedValue } from '@/shared/lib/hooks/useDebouncedValue';
import { useHotkey } from '@/shared/lib/hooks/useHotkey';
import { matchesQuery, normalizeQuery } from '@/shared/lib/strings';
import { useModuleRegistry } from '@/shared/registry/context';
import { isNavItemVisible } from '@/shared/registry/navVisibility';
import type { CommandItem as PaletteItem, CommandSource } from '@/shared/registry/types';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/shared/ui/command';
import { Kbd } from '@/shared/ui/kbd';
import { Spinner } from '@/shared/ui/spinner';

import { bySidebarOrder, PEOPLE_SHOWN, personMatches, useLiveSelection } from './palette';
import { useShellStore } from './shellStore';
import { signOut } from './signOut';

function ResultItem({ item, onRun }: { item: PaletteItem; onRun: (item: PaletteItem) => void }) {
  const Icon = item.icon;
  return (
    <CommandItem value={item.id} onSelect={() => onRun(item)}>
      {item.leading ?? (Icon ? <Icon /> : <ArrowRightIcon />)}
      <span className="min-w-0 flex-1">
        <span className="block truncate">{item.label}</span>
        {item.description && (
          <span className="block truncate text-xs text-muted-foreground">{item.description}</span>
        )}
      </span>
    </CommandItem>
  );
}

/** Renders one module-contributed source; its hook runs only while the palette is open. */
function SourceGroup({
  source,
  query,
  onRun,
}: {
  source: CommandSource;
  query: string;
  onRun: (item: PaletteItem) => void;
}) {
  const { t } = useTranslation();
  const { items, isLoading } = source.useItems(query);
  if (items.length === 0 && !isLoading) return null;
  return (
    <CommandGroup heading={t(source.headingKey)}>
      {isLoading && items.length === 0 ? (
        <div className="flex items-center gap-2 px-2 py-2 text-sm text-muted-foreground">
          <Spinner className="size-3.5" />
        </div>
      ) : (
        items.map((item) => <ResultItem key={item.id} item={item} onRun={onRun} />)
      )}
    </CommandGroup>
  );
}

function PeopleGroup({ query, onRun }: { query: string; onRun: (item: PaletteItem) => void }) {
  const { t } = useTranslation('app');
  const registry = useModuleRegistry();
  const { open } = useSidePanel();
  const search = normalizeQuery(query);
  const enabled = (search?.length ?? 0) >= 2;
  const result = useUserSearch({ search, pageSize: PEOPLE_SHOWN + 4 }, { enabled });
  if (!enabled) return null;
  const found = result.data?.items ?? [];
  const users = found.filter((user) => personMatches(user, query)).slice(0, PEOPLE_SHOWN);
  if (users.length === 0 && (!result.isFetching || found.length > 0)) return null;
  const canOpenPanel = registry.hasSidePanel('user');
  return (
    <CommandGroup heading={t('palette.people')}>
      {users.length === 0 ? (
        <div className="flex items-center gap-2 px-2 py-2 text-sm text-muted-foreground">
          <Spinner className="size-3.5" />
        </div>
      ) : (
        users.map((user) => (
          <ResultItem
            key={user.id}
            item={{
              id: `user:${user.id}`,
              label: user.fullName,
              description: [user.position, user.departmentName].filter(Boolean).join(' · ') || undefined,
              leading: <Avatar user={user} size="sm" showPresence />,
              ...(canOpenPanel
                ? { perform: () => open('user', user.id) }
                : { to: `/company/users/${user.id}` }),
            }}
            onRun={onRun}
          />
        ))
      )}
    </CommandGroup>
  );
}

/** ⌘K command palette: navigation, people, module-contributed results and quick actions. */
export function CommandPalette() {
  const { t } = useTranslation('app');
  const { t: tAny } = useTranslation();
  const navigate = useNavigate();
  const registry = useModuleRegistry();
  const me = useCurrentUserOrNull();
  const open = useShellStore((s) => s.paletteOpen);
  const setOpen = useShellStore((s) => s.setPaletteOpen);
  const [query, setQuery] = useState('');
  const { listRef, ...selection } = useLiveSelection();
  const debounced = useDebouncedValue(query, 150);

  useHotkey('k', () => setOpen(!useShellStore.getState().paletteOpen), { mod: true });

  const run = (item: PaletteItem) => {
    setOpen(false);
    setQuery('');
    if (item.perform) item.perform();
    else if (item.to) void navigate(item.to);
  };

  const navigation = useMemo<PaletteItem[]>(
    () =>
      registry.navItems
        .filter((item) => isNavItemVisible(item, me))
        .sort(bySidebarOrder)
        .map((item) => ({
          id: `nav:${item.id}`,
          label: tAny(item.labelKey),
          icon: item.icon,
          to: item.to,
          // Module ids and paths match in every UI language ("tasks", "/calendar").
          keywords: [item.id, item.to],
        })),
    [registry.navItems, me, tAny],
  );

  const actions: PaletteItem[] = [
    { id: 'theme:light', label: t('palette.themeLight'), icon: SunIcon, perform: () => changeTheme('light') },
    { id: 'theme:dark', label: t('palette.themeDark'), icon: MoonIcon, perform: () => changeTheme('dark') },
    {
      id: 'theme:system',
      label: t('palette.themeSystem'),
      icon: MonitorIcon,
      perform: () => changeTheme('system'),
    },
    ...SUPPORTED_LANGUAGES.map<PaletteItem>((lng) => ({
      id: `lang:${lng}`,
      label: t('palette.language', { language: LANGUAGES[lng].nativeName }),
      icon: LanguagesIcon,
      keywords: [lng, LANGUAGES[lng].nativeName],
      perform: () => changeLanguage(lng),
    })),
    { id: 'logout', label: t('userMenu.logout'), icon: LogOutIcon, perform: signOut },
  ];

  const filteredNav = navigation.filter((item) => matchesQuery(query, item.label, ...(item.keywords ?? [])));
  const filteredActions = actions.filter((item) => matchesQuery(query, item.label, ...(item.keywords ?? [])));
  const sources = registry.commandSources.filter((s) => debounced.trim().length >= (s.minQueryLength ?? 0));
  // A source may show a row of its own (the search filters) while the query is long enough for it.
  const toolbars = registry.commandSources.filter(
    (s) => s.toolbar && query.trim().length >= (s.minQueryLength ?? 0),
  );

  // Whatever the way the palette closes (Esc, a choice, the shortcut again), the sources forget what their toolbars set.
  useEffect(() => {
    if (open) return;
    for (const source of registry.commandSources) source.onClose?.();
  }, [open, registry.commandSources]);
  const renderSources = (list: CommandSource[]) =>
    list.map((source) => (
      <Fragment key={source.id}>
        <SourceGroup source={source} query={debounced} onRun={run} />
      </Fragment>
    ));

  const groups: ReactNode[] = [];
  if (filteredNav.length > 0) {
    groups.push(
      <CommandGroup key="nav" heading={t('palette.navigation')}>
        {filteredNav.map((item) => (
          <ResultItem key={item.id} item={item} onRun={run} />
        ))}
      </CommandGroup>,
    );
  }

  return (
    <CommandDialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setQuery('');
      }}
      title={t('palette.title')}
      commandProps={{ shouldFilter: false, loop: true, label: t('palette.title'), ...selection }}
    >
      <CommandInput
        value={query}
        onValueChange={setQuery}
        placeholder={t('palette.placeholder')}
        trailing={<Kbd>Esc</Kbd>}
      />
      {toolbars.map((source) => (source.toolbar ? <source.toolbar key={source.id} /> : null))}
      <CommandList ref={listRef}>
        {groups}
        {open && renderSources(sources.filter((source) => source.first))}
        {open && <PeopleGroup query={debounced} onRun={run} />}
        {open && renderSources(sources.filter((source) => !source.first))}
        {filteredActions.length > 0 && (
          <CommandGroup heading={t('palette.actions')}>
            {filteredActions.map((item) => (
              <ResultItem key={item.id} item={item} onRun={run} />
            ))}
          </CommandGroup>
        )}
        <CommandEmpty>{t('palette.empty')}</CommandEmpty>
      </CommandList>
      <div className="flex items-center gap-4 border-t px-4 py-2 text-xs text-muted-foreground">
        <span className="flex items-center gap-1.5">
          <Kbd>↑</Kbd>
          <Kbd>↓</Kbd>
          {t('palette.hintNavigate')}
        </span>
        <span className="flex items-center gap-1.5">
          <Kbd>
            <CornerDownLeftIcon className="size-3" />
          </Kbd>
          {t('palette.hintOpen')}
        </span>
      </div>
    </CommandDialog>
  );
}
